import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import OpenSeadragon from 'openseadragon';
import { fabric } from 'fabric';
// import { fabric } from '../../../helpers/fabricjs';
import initFabricJSOverlay from '../helpers/overlay';
import {
  updateOverlay,
  updateTool,
} from '../../../redux/actions/fabric_overlay';
import { getStatusColor } from './Task/taskStatusColors';
import { isEmpty } from 'lodash';
import CursorTooltip from './CursorTooltip/CursorTooltip';

export default function Viewer({
  plan,
  onSubmit,
  fabricOverlay,
  annotations,
  planVersionTasks,
}) {
  const viewerRef = useRef(null);
  const isLoadingRef = useRef(true);

  let controlPointRatio = 14;
  let pixelToMmRatio = 7.874;
  let taskPinSizeRatio = 0.5;

  useEffect(() => {
    if (!plan || !viewerRef.current) return;

    // Init Fabric styling
    fabric.Object.prototype.set({
      borderColor: '#22a2f8',
      cornerColor: '#22a2f8',
      cornerStrokeColor: '#22a2f8',
      cornerSize: 10,
      cornerStyle: 'rect',
      transparentCorners: true,
      borderScaleFactor: 1,
    });

    fabric.Canvas.prototype.set({
      perPixelTargetFind: true,
      targetFindTolerance: 5,
    });

    //fabric.Object.prototype.moveCursor = 'default';

    // Init overlay
    initFabricJSOverlay(OpenSeadragon, fabric);

    // Init OSD
    const viewer = OpenSeadragon({
      element: viewerRef.current,
      tileSources: {
        height: plan?.height,
        width: plan?.width,
        tileSize: 256,
        getTileUrl: (level, x, y) => plan?.tiles?.[`${level}-${x}-${y}`],
      },
      debugMode: false,
      gestureSettingsMouse: {
        clickToZoom: false,
        flickEnabled: true,
        pinchToZoom: true,
        scrollToZoom: true,
      },
      // gestureSettingsTouch: {
      //   clickToZoom: true,
      //   flickEnabled: true,
      //   pinchToZoom: true,
      //   scrollToZoom: true,
      // },
      gestureSettingsTouch: {
        clickToZoom: false,
        flickEnabled: true,
        pinchToZoom: true,
        scrollToZoom: true,
      },
      gestureSettingsMouse: {
        clickToZoom: false,
      },
      showNavigationControl: false,
      defaultZoomLevel: 0,
      minZoomLevel: 0,
      constrainDuringPan: true,
      maxZoomLevel: 14, // maksimalni zoom (npr. 20x)
      visibilityRatio: 0.98,
      animationTime: 0.5,
    });

    const fabricOverlay = viewer.fabricjsOverlay({
      scale: 1,
    });

    //const viewer = osdInstanceRef?.current;
    const canvas = fabricOverlay.fabricCanvas();

    // Set background color
    viewer.drawer.canvas.style.background = '#f2f2f0';

    // Add toolbar element to OSD viewport
    const toolbarElement = document.getElementById('toolbar');

    if (toolbarElement && viewer) {
      viewer.container.appendChild(toolbarElement);
    }

    // Set overlay to be visible in other components like Draw, Circle...
    updateOverlay({
      fabricOverlay,
      viewer,
    });

    // Fabric events
    let objectWasModified = false;

    const clampObjectPosition = (obj) => {
      obj.setCoords();

      if (obj.objectType == 'task') {
        const zoom = viewer.viewport.getZoom();
        const scale = (pixelToMmRatio / zoom) * taskPinSizeRatio;

        obj.originalLeft = obj.left + (obj.width * scale) / 2;
        obj.originalTop = obj.top + obj.height * scale;

        const width = obj.width * scale;
        const height = obj.height * scale;

        if (obj.originalLeft < width / 2) {
          obj.originalLeft = width / 2;
        }
        if (obj.originalLeft > plan.width - width / 2) {
          obj.originalLeft = plan.width - width / 2;
        }
        if (obj.originalTop < height) {
          obj.originalTop = height;
        }
        if (obj.originalTop > plan.height) {
          obj.originalTop = plan.height;
        }

        // obj.set({
        //   // scaleX: scale,
        //   // scaleY: scale,
        //   left: obj.originalLeft - width / 2,
        //   top: obj.originalTop - height,
        // });

        // obj.setCoords();
      } else {
        // obj.setCoords();

        // let deltaX = 0;
        // let deltaY = 0;

        // if (bounds.left < 0) deltaX = -bounds.left;
        // if (bounds.top < 0) deltaY = -bounds.top;
        // if (bounds.left + bounds.width > plan.width)
        //   deltaX = plan.width - (bounds.left + bounds.width);
        // if (bounds.top + bounds.height > plan.height)
        //   deltaY = plan.height - (bounds.top + bounds.height);

        // obj.left += deltaX;
        // obj.top += deltaY;

        // obj.setCoords();

        // obj.lastGoodLeft = obj.left;
        // obj.lastGoodTop = obj.top;
        // obj.lastGoodScaleX = obj.scaleX;
        // obj.lastGoodScaleY = obj.scaleY;

        const bounds = obj.getBoundingRect(true);

        if (
          bounds.left < 0 ||
          bounds.top < 0 ||
          bounds.left + bounds.width > plan.width ||
          bounds.top + bounds.height > plan.height
        ) {
          obj.scaleX = obj.lastGoodScaleX ?? 1;
          obj.scaleY = obj.lastGoodScaleY ?? 1;
          obj.left = obj.lastGoodLeft ?? obj.left;
          obj.top = obj.lastGoodTop ?? obj.top;
        } else {
          obj.lastGoodLeft = obj.left;
          obj.lastGoodTop = obj.top;
          obj.lastGoodScaleX = obj.scaleX;
          obj.lastGoodScaleY = obj.scaleY;
        }

        obj.setCoords();
      }
    };

    const handleMouseUp = (e) => {
      if (objectWasModified) {
        onSubmit(fabricOverlay);
        objectWasModified = false;
      }
    };

    const handleObjectMoving = (e) => {
      objectWasModified = true;
      clampObjectPosition(e.target);
    };

    const handleObjectScaling = (e) => {
      objectWasModified = true;
      clampObjectPosition(e.target);
    };

    const handleObjectAdded = (e) => {
      if (isLoadingRef.current) return;

      if (e.target?.objectType === 'polygon') {
        return onSubmit(fabricOverlay);
      }

      if (e.target?.objectType != 'controlpoint') {
        objectWasModified = true;
      }
    };

    const handleBeforeRender = () => {
      if (viewer.source) {
        let planWidth = viewer.source.width;
        let planHeight = viewer.source.height;

        const c = viewer.viewport.getContainerSize();
        const wr = planWidth / c.x;
        const rr = wr;
        taskPinSizeRatio = rr / 20;
        controlPointRatio = rr * 1.8;
      }

      const zoom = viewer.viewport.getZoom();
      const w = (pixelToMmRatio / zoom) * controlPointRatio;

      const objects = canvas.getObjects();

      for (let i = 0; i < objects.length; i++) {
        const obj = objects[i];
        if (obj.objectType === 'controlpoint' && !obj.isTemporary) {
          obj.set({
            left: obj.left + obj.width / 2 - w / 2,
            top: obj.top + obj.height / 2 - w / 2,
            width: w,
            height: w,
            strokeWidth: w / 20,
          });
        }
        if (obj.objectType === 'task') {
          obj.set({
            scaleX: (pixelToMmRatio / zoom) * taskPinSizeRatio,
            scaleY: (pixelToMmRatio / zoom) * taskPinSizeRatio,
            left:
              obj.originalLeft -
              (((obj.width / 2) * pixelToMmRatio) / zoom) * taskPinSizeRatio,
            top:
              obj.originalTop -
              ((obj.height * pixelToMmRatio) / zoom) * taskPinSizeRatio,
          });
        }
      }
    };

    canvas.on('mouse:up', handleMouseUp);
    canvas.on('object:moving', handleObjectMoving);
    canvas.on('object:added', handleObjectAdded);
    canvas.on('object:scaling', handleObjectScaling);
    canvas.on('before:render', handleBeforeRender);

    return () => {
      objectWasModified = false;
      viewer.destroy();
      //viewer = null;
      canvas.off('mouse:up', handleMouseUp);
      canvas.off('object:moving', handleObjectMoving);
      canvas.off('object:added', handleObjectAdded);
      canvas.off('object:scaling', handleObjectScaling);
      canvas.off('before:render', handleBeforeRender);

      updateTool({ tool: '' });
    };
  }, [plan]);

  useEffect(() => {
    if (!fabricOverlay || !annotations?.objects) return;

    const canvas = fabricOverlay.fabricCanvas();
    isLoadingRef.current = true;

    if (isEmpty(annotations.objects)) {
      isLoadingRef.current = false;
      updateTool({ tool: 'hand' });
      return;
    }

    const promises = annotations?.objects.map((annotation) => {
      fabric.util.enlivenObjects([annotation], (objects) => {
        return new Promise((resolve) => {
          objects.forEach((obj) => {
            if (obj.objectType === 'line') {
              const dx = Math.abs(obj.x1);
              const dy = Math.abs(obj.y1);
              const { left, top } = obj;

              obj.x1 = obj.x1 + left + dx;
              obj.x2 = obj.x2 + left + dx;
              obj.y1 = obj.y1 + top + dy;
              obj.y2 = obj.y2 + top + dy;

              obj.left = left;
              obj.top = top;
              obj.startLeft = left;
              obj.startTop = top;

              obj.setCoords();
            }

            if (obj.objectType === 'path') {
              obj.lockScalingX = true;
              obj.lockScalingY = true;
              obj.hasControls = true;
              obj.hasBorders = true;
              obj.perPixelTargetFind = true;

              obj.setCoords();
            }

            if (obj.objectType === 'task') {
              const status = planVersionTasks[obj.taskID];
              let color = getStatusColor(status);

              obj.fill = color;
              obj.stroke = color;
            }

            canvas.add(obj);
          });

          resolve();
        });
      });
    });

    Promise.all(promises).then(() => {
      isLoadingRef.current = false;
      updateTool({ tool: 'hand' }); // ✅ pozovi tek kada su svi objekti gotovi
    });
  }, [fabricOverlay, annotations]);

  return (
    <div ref={viewerRef} style={{ width: '100%', height: '100%' }}>
      <CursorTooltip />
    </div>
  );
}

Viewer.propTypes = {
  plan: PropTypes.object,
};
